import { useEffect, useRef, useState } from 'react';
import { supabase, GUARDAR_USUARIO_FUNCTION_URL, LISTAR_USUARIOS_FUNCTION_URL, SUPABASE_ANON_KEY } from './supabase';
import type { Consumo, Notificaciones, Perfil, Preferencias, RolPermiso, Sucursal, Usuario } from './types';

// ── DATA LAYER SOBRE TABLAS REALES DE SUPABASE ────────────────────────────────
// Cada entidad vive en su propia tabla. Mapeamos camelCase (app) <-> snake_case
// (Postgres). localStorage se usa SOLO como caché para render instantáneo y
// respaldo offline; la fuente de verdad es Supabase.

type Row = Record<string, any>;
function readCache<T>(key: string): T[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T[] : null;
  } catch {
    return null;
  }
}
function writeCache<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {

    /* ignore */}
}
interface TableConfig<T> {
  table: string;
  localKey: string;
  mapRow: (row: Row) => T;
  toRow: (item: T) => Row;
  getId?: (item: T) => string;
  seed?: T[];
  orderBy?: {
    column: string;
    ascending?: boolean;
  };
}
export type ArraySetter<T> = React.Dispatch<React.SetStateAction<T[]>>;

// Hook genérico tipo useState que sincroniza un arreglo con una tabla.
function useSupabaseTable<T>(cfg: TableConfig<T>): [T[], ArraySetter<T>] {
  const getId = cfg.getId ?? ((x: any) => x.id as string);
  const [items, setItems] = useState<T[]>(() => readCache<T>(cfg.localKey) ?? cfg.seed ?? []);
  const prevRef = useRef<T[]>(items);
  const ready = useRef(false);
  // Ids existentes al montar (caché/seed). Sirven para distinguir registros
  // que el usuario crea DURANTE la carga inicial (que sí hay que conservar)
  // de los datos viejos de caché (que el servidor debe sobrescribir).
  const initialIdsRef = useRef<Set<string>>(new Set(items.map(getId)));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let query = supabase.from(cfg.table).select('*');
        if (cfg.orderBy) {
          query = query.order(cfg.orderBy.column, {
            ascending: cfg.orderBy.ascending ?? true
          });
        }
        const {
          data,
          error
        } = await query;
        if (cancelled) return;
        if (error) {
          // Sin conexión / sin tabla: mantenemos la caché local.
          ready.current = true;
          return;
        }
        let rows = (data ?? []).map(cfg.mapRow);
        // Sembrar la tabla solo la primera vez (cuando está vacía).
        if (rows.length === 0 && cfg.seed && cfg.seed.length) {
          await supabase.from(cfg.table).upsert(cfg.seed.map(cfg.toRow));
          rows = cfg.seed;
        }
        // Fusionar: si el usuario creó registros mientras cargábamos (no estaban
        // en la caché inicial ni vienen del servidor), conservarlos y empujarlos
        // a Supabase en vez de descartarlos al sobrescribir con los del servidor.
        setItems(local => {
          const serverIds = new Set(rows.map(getId));
          const nuevosLocales = local.filter(it => !serverIds.has(getId(it)) && !initialIdsRef.current.has(getId(it)));
          if (nuevosLocales.length) {
            void supabase.from(cfg.table).upsert(nuevosLocales.map(cfg.toRow));
          }
          const merged = [...rows, ...nuevosLocales];
          prevRef.current = merged;
          writeCache(cfg.localKey, merged);
          return merged;
        });
      } catch {

        /* offline: caché */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.table]);
  const sync = async (prev: T[], next: T[]) => {
    try {
      // upsert de todo lo presente (idempotente)
      if (next.length) {
        await supabase.from(cfg.table).upsert(next.map(cfg.toRow));
      }
      // eliminar lo que ya no está
      const nextIds = new Set(next.map(getId));
      const removed = prev.map(getId).filter(id => !nextIds.has(id));
      if (removed.length) {
        await supabase.from(cfg.table).delete().in('id', removed);
      }
    } catch {

      /* offline: la caché local ya está actualizada, reintentará al recargar */}
  };
  const update: ArraySetter<T> = v => {
    setItems(prev => {
      const next = typeof v === 'function' ? (v as (p: T[]) => T[])(prev) : v;
      writeCache(cfg.localKey, next);
      if (ready.current) void sync(prevRef.current, next);
      prevRef.current = next;
      return next;
    });
  };
  return [items, update];
}

// ── HOOKS POR ENTIDAD ─────────────────────────────────────────────────────────

export function useSucursales(seed: Sucursal[]) {
  return useSupabaseTable<Sucursal>({
    table: 'sucursales',
    localKey: 'getr.sucursales',
    seed,
    mapRow: r => ({
      id: r.id,
      nombre: r.nombre ?? '',
      direccion: r.direccion ?? '',
      tipo: r.tipo ?? '',
      creado: Number(r.creado ?? Date.now()),
      archivado: !!r.archivado
    }),
    toRow: s => ({
      id: s.id,
      nombre: s.nombre,
      direccion: s.direccion,
      tipo: s.tipo,
      creado: s.creado,
      archivado: !!s.archivado
    })
  });
}

// ── USUARIOS: caso especial, sin acceso directo por RLS ──────────────────────
// La tabla `usuarios` tiene RLS estricto (ver RLS_USUARIOS_SQL): el cliente
// anónimo NO puede hacer select/insert/update directo. Todo pasa por Edge
// Functions ('login' para autenticar, 'guardar-usuario' para crear/editar),
// que usan la service_role key solo disponible en el servidor.
//
// Por eso este hook no reusa useSupabaseTable: en vez de eso, expone
// `guardarUsuario` (llama a la Edge Function) además del setter normal,
// que solo debe usarse para cambios que NO tocan password (archivar, etc.)
// y que de todas formas Supabase rechazará si el RLS está bien configurado.
export function useUsuarios(seed: Usuario[]) {
  const [items, setItems] = useState<Usuario[]>(() => readCache<Usuario>('getr.usuarios') ?? seed);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(LISTAR_USUARIOS_FUNCTION_URL, {
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`
          }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const rows: Row[] = Array.isArray(data?.usuarios) ? data.usuarios : [];
        const mapped: Usuario[] = rows.map(r => ({
          id: r.id,
          nombre: r.nombre ?? '',
          correo: r.correo ?? '',
          rol: (r.rol ?? 'tecnico') as Usuario['rol'],
          sucursalesIds: Array.isArray(r.sucursales_ids) ? r.sucursales_ids : [],
          archivado: !!r.archivado
        }));
        if (mapped.length && !cancelled) {
          setItems(mapped);
          writeCache('getr.usuarios', mapped);
        }
      } catch {

        /* offline: nos quedamos con la caché/seed local */}
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    writeCache('getr.usuarios', items);
  }, [items]);
  return [items, setItems] as [Usuario[], ArraySetter<Usuario>];
}

// Crea o edita un usuario llamando a la Edge Function 'guardar-usuario'.
// La contraseña en texto plano (si se pasa) viaja por HTTPS y se hashea
// en el servidor — nunca se guarda ni se muestra en claro en el cliente.
export async function guardarUsuario(input: {
  id: string;
  nombre: string;
  correo: string;
  password?: string;
  rol: Usuario['rol'];
  sucursalesIds: string[];
  archivado?: boolean;
}): Promise<{
  ok: boolean;
  error?: string;
}> {
  try {
    const res = await fetch(GUARDAR_USUARIO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(input)
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        error: data?.error ?? 'No se pudo guardar el usuario.'
      };
    }
    return {
      ok: true
    };
  } catch {
    return {
      ok: false,
      error: 'Sin conexión al servidor.'
    };
  }
}
export function useRoles(seed: RolPermiso[]) {
  return useSupabaseTable<RolPermiso>({
    table: 'roles',
    localKey: 'getr.roles',
    seed,
    mapRow: r => ({
      id: r.id,
      nombre: r.nombre ?? '',
      descripcion: r.descripcion ?? '',
      permisos: r.permisos ?? {},
      archivado: !!r.archivado
    }),
    toRow: r => ({
      id: r.id,
      nombre: r.nombre,
      descripcion: r.descripcion,
      permisos: r.permisos,
      archivado: !!r.archivado
    })
  });
}

// Consumo NO se siembra: empieza vacío para que entres tus lecturas reales.
export function useConsumos() {
  return useSupabaseTable<Consumo>({
    table: 'consumo',
    localKey: 'getr.consumos',
    orderBy: {
      column: 'fecha',
      ascending: true
    },
    mapRow: r => ({
      id: r.id,
      sucursalId: r.sucursal_id ?? '',
      fecha: Number(r.fecha ?? Date.now()),
      kwh: Number(r.kwh ?? 0),
      archivado: !!r.archivado
    }),
    toRow: c => ({
      id: c.id,
      sucursal_id: c.sucursalId,
      fecha: c.fecha,
      kwh: c.kwh,
      archivado: !!c.archivado
    })
  });
}

// ── AUDITORÍA (log append-only) ───────────────────────────────────────────────
export function useAuditoria(stamp: () => string) {
  const [audit, setAudit] = useState<string[]>(() => readCache<string>('getr.auditoria') ?? []);
  const ready = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('auditoria').select('mensaje').order('id', {
          ascending: false
        }).limit(80);
        if (cancelled) return;
        if (!error && data) {
          const list = data.map((r: Row) => r.mensaje as string);
          setAudit(list);
          writeCache('getr.auditoria', list);
        }
      } catch {

        /* offline */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const log = (msg: string) => {
    const full = `${stamp()} - ${msg}`;
    setAudit(prev => {
      const next = [full, ...prev].slice(0, 80);
      writeCache('getr.auditoria', next);
      return next;
    });
    void supabase.from('auditoria').insert({
      mensaje: full
    }).then(() => undefined);
  };
  return {
    audit,
    log
  };
}

// ── CONFIGURACIÓN (fila única) ────────────────────────────────────────────────
interface ConfigState {
  perfil: Perfil;
  notif: Notificaciones;
  prefs: Preferencias;
}
export function useConfiguracion(defaults: ConfigState, userId: string) {
  const cacheKey = `getr.perfil.${userId}`;
  const cacheKeyNotif = `getr.notificaciones.${userId}`;
  const cacheKeyPrefs = `getr.preferencias.${userId}`;
  const [perfil, setPerfilState] = useState<Perfil>(() => readCache<Perfil>(cacheKey)?.[0] ?? defaults.perfil);
  const [notif, setNotifState] = useState<Notificaciones>(() => readCache<Notificaciones>(cacheKeyNotif)?.[0] ?? defaults.notif);
  const [prefs, setPrefsState] = useState<Preferencias>(() => readCache<Preferencias>(cacheKeyPrefs)?.[0] ?? defaults.prefs);
  const ready = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<ConfigState>({
    perfil,
    notif,
    prefs
  });
  latest.current = {
    perfil,
    notif,
    prefs
  };
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('configuracion').select('*').eq('id', userId).maybeSingle();
        if (cancelled) return;
        if (!error) {
          if (data) {
            if (data.perfil) setPerfilState(data.perfil);
            if (data.notificaciones) setNotifState(data.notificaciones);
            if (data.preferencias) setPrefsState(data.preferencias);
          } else {
            // Sembrar fila por usuario.
            await supabase.from('configuracion').upsert({
              id: userId,
              perfil: defaults.perfil,
              notificaciones: defaults.notif,
              preferencias: defaults.prefs
            });
          }
        }
      } catch {

        /* offline */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const persist = () => {
    if (!ready.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void supabase.from('configuracion').upsert({
        id: userId,
        perfil: latest.current.perfil,
        notificaciones: latest.current.notif,
        preferencias: latest.current.prefs
      });
    }, 400);
  };
  const setPerfil = (value: Perfil) => {
    setPerfilState(value);
    writeCache(cacheKey, [value]);
    persist();
  };
  const setNotif = (value: Notificaciones) => {
    setNotifState(value);
    writeCache(cacheKeyNotif, [value]);
    persist();
  };
  const setPrefs = (value: Preferencias) => {
    setPrefsState(value);
    writeCache(cacheKeyPrefs, [value]);
    persist();
  };
  return {
    perfil,
    notif,
    prefs,
    setPerfil,
    setNotif,
    setPrefs
  };
}